import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";

function tiempoRelativo(fechaIso) {
  const diffMs = Date.now() - new Date(fechaIso).getTime();
  const minutos = Math.floor(diffMs / 60000);
  if (minutos < 1) return "ahora mismo";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `hace ${dias} d`;
}

function CampanaNotificaciones() {
  const [abierto, setAbierto] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const contenedorRef = useRef(null);
  const navigate = useNavigate();

  const cargar = () => {
    apiFetch("/api/notificaciones")
      .then((data) => {
        setNotificaciones(data?.notificaciones || []);
        setNoLeidas(data?.no_leidas || 0);
      })
      .catch(() => {});
  };

  useEffect(() => {
    cargar();
    const intervalo = setInterval(cargar, 60000); // refresca cada minuto
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    const cerrarSiFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener("mousedown", cerrarSiFuera);
    return () => document.removeEventListener("mousedown", cerrarSiFuera);
  }, []);

  const abrirNotificacion = async (n) => {
    if (!n.leida) {
      apiFetch(`/api/notificaciones/${n.id}/leida`, { method: "POST", body: JSON.stringify({}) }).catch(
        () => {}
      );
      setNotificaciones((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: 1 } : x)));
      setNoLeidas((prev) => Math.max(0, prev - 1));
    }
    setAbierto(false);
    navigate(`/solicitud/${n.solicitud_id}`);
  };

  const marcarTodasLeidas = async () => {
    try {
      await apiFetch("/api/notificaciones/marcar-todas", { method: "POST", body: JSON.stringify({}) });
      setNotificaciones((prev) => prev.map((x) => ({ ...x, leida: 1 })));
      setNoLeidas(0);
    } catch {
      // silencioso
    }
  };

  return (
    <div style={{ position: "relative" }} ref={contenedorRef}>
      <button
        onClick={() => setAbierto((v) => !v)}
        style={styles.botonCampana}
        aria-label="Notificaciones"
      >
        🔔
        {noLeidas > 0 && <span style={styles.contador}>{noLeidas > 9 ? "9+" : noLeidas}</span>}
      </button>

      {abierto && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            <strong style={{ fontSize: "0.9rem" }}>Notificaciones</strong>
            {noLeidas > 0 && (
              <button onClick={marcarTodasLeidas} style={styles.marcarTodas}>
                Marcar todas leídas
              </button>
            )}
          </div>

          {notificaciones.length === 0 ? (
            <p style={styles.vacio}>No tienes notificaciones todavía.</p>
          ) : (
            <div style={{ maxHeight: 340, overflowY: "auto" }}>
              {notificaciones.map((n) => (
                <button
                  key={n.id}
                  onClick={() => abrirNotificacion(n)}
                  style={{
                    ...styles.itemNotif,
                    background: n.leida ? "#fff" : "var(--tema-suave, #eef4fb)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    {!n.leida && <span style={styles.puntoNoLeido} />}
                    <strong style={{ fontSize: "0.83rem" }}>{n.titulo}</strong>
                  </div>
                  <p style={styles.itemMensaje}>{n.mensaje}</p>
                  <span style={styles.itemFecha}>{tiempoRelativo(n.creado_en)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  botonCampana: {
    position: "relative",
    background: "transparent",
    border: "1px solid #d3d9df",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 8,
    padding: "0.35rem 0.6rem",
    cursor: "pointer",
    fontSize: "1rem",
    lineHeight: 1,
  },
  contador: {
    position: "absolute",
    top: -6,
    right: -6,
    background: "#a3402f",
    color: "#fff",
    borderRadius: 999,
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "0.1rem 0.35rem",
    minWidth: 16,
    textAlign: "center",
  },
  dropdown: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 8px)",
    width: "min(320px, 90vw)",
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(27,42,74,0.25)",
    zIndex: 1000,
    overflow: "hidden",
  },
  dropdownHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.7rem 0.9rem",
    borderBottom: "1px solid #eef1f3",
  },
  marcarTodas: {
    background: "none",
    border: "none",
    color: "var(--tema-primario, #2f6690)",
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  vacio: {
    padding: "1.2rem 0.9rem",
    fontSize: "0.85rem",
    color: "#5b6472",
    textAlign: "center",
  },
  itemNotif: {
    display: "block",
    width: "100%",
    textAlign: "left",
    border: "none",
    borderBottom: "1px solid #eef1f3",
    padding: "0.6rem 0.9rem",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  puntoNoLeido: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "var(--tema-primario, #2f6690)",
    display: "inline-block",
    flexShrink: 0,
  },
  itemMensaje: {
    margin: "0.2rem 0 0.25rem",
    fontSize: "0.78rem",
    color: "#3a4356",
    lineHeight: 1.35,
  },
  itemFecha: { fontSize: "0.7rem", color: "#9aa3b0" },
};

export default CampanaNotificaciones;
